import { makeAutoObservable, runInAction } from "mobx";
import { collection, onSnapshot, Unsubscribe } from "firebase/firestore";
import { db } from "../Firebase";
import {
    BUILTIN_LAYOUT_PACK,
    DEFAULT_LAYOUT_PACK_ID,
    type CanvasAspectPreset,
    type LayoutPack,
    type LayoutTemplate,
    type LayoutTemplateType,
    type LayoutPreview,
    type MulticamVariant,
} from "../librettoEditor/layouts/LayoutPacks";
import { kLayoutPacksCollectionName } from "../utils/CollectionConstants";

type SubscribeOptions = {
    workspaceId?: string;
    userId?: string;
    onAuthError?: () => void;
};

const PRESETS: CanvasAspectPreset[] = ["horizontal", "vertical", "square"];

const toStringOrNull = (v: any): string | null => {
    const s = String(v ?? "").trim();
    return s ? s : null;
};

const asPreset = (key: string): CanvasAspectPreset | null => {
    const k = String(key ?? "").trim().toLowerCase();
    if (k === "horizontal" || k === "vertical" || k === "square") return k;
    return null;
};

const parseLayoutPreview = (value: any): LayoutPreview | null => {
    if (!value || typeof value !== "object") return null;
    const muxPlaybackId = toStringOrNull((value as any).muxPlaybackId ?? (value as any).playbackId);
    const thumbnailUrl = toStringOrNull(
        (value as any).thumbnailUrl ??
            (value as any).posterUrl ??
            (value as any).imageUrl ??
            (value as any).thumbnail ??
            (value as any).url
    );
    if (!muxPlaybackId && !thumbnailUrl) return null;
    const objectFitRaw = toStringOrNull((value as any).objectFit);
    const objectFit =
        objectFitRaw === "cover" || objectFitRaw === "contain" ? (objectFitRaw as any) : undefined;
    return {
        muxPlaybackId: muxPlaybackId ?? undefined,
        thumbnailUrl: thumbnailUrl ?? undefined,
        objectFit,
    };
};

const parseTemplate = (id: string, value: any): LayoutTemplate | null => {
    if (!id) return null;
    const data = value && typeof value === "object" ? value : {};
    const title = toStringOrNull((data as any).title ?? (data as any).name) ?? id;
    const type = (toStringOrNull((data as any).type) ?? "Other") as LayoutTemplateType;

    const previewsByPreset: Partial<Record<CanvasAspectPreset, LayoutPreview>> = {};
    const rawPreviews = (data as any).previewsByPreset ?? (data as any).previews ?? null;
    if (rawPreviews && typeof rawPreviews === "object") {
        for (const [k, v] of Object.entries(rawPreviews)) {
            const preset = asPreset(k);
            if (!preset) continue;
            const preview = parseLayoutPreview(v);
            if (!preview) continue;
            previewsByPreset[preset] = preview;
        }
    }

    return {
        id,
        title,
        type,
        previewsByPreset: Object.keys(previewsByPreset).length > 0 ? previewsByPreset : undefined,
    };
};

const parseMulticamVariantsByPreset = (raw: any): Partial<Record<CanvasAspectPreset, MulticamVariant[]>> | undefined => {
    if (!raw || typeof raw !== "object") return undefined;
    const out: Partial<Record<CanvasAspectPreset, MulticamVariant[]>> = {};
    for (const [k, v] of Object.entries(raw)) {
        const preset = asPreset(k);
        if (!preset) continue;
        if (!Array.isArray(v)) continue;
        const items: MulticamVariant[] = [];
        for (const item of v) {
            if (!item || typeof item !== "object") continue;
            const key = toStringOrNull((item as any).key);
            const label = toStringOrNull((item as any).label ?? (item as any).title);
            const muxPlaybackId = toStringOrNull((item as any).muxPlaybackId ?? (item as any).playbackId);
            const thumbnailUrl = toStringOrNull(
                (item as any).thumbnailUrl ??
                    (item as any).posterUrl ??
                    (item as any).imageUrl ??
                    (item as any).thumbnail ??
                    (item as any).url
            );
            if (!key || !label) continue;
            if (!muxPlaybackId && !thumbnailUrl) continue;
            items.push({
                key,
                label,
                muxPlaybackId: muxPlaybackId ?? undefined,
                thumbnailUrl: thumbnailUrl ?? undefined,
            });
        }
        if (items.length) out[preset] = items;
    }
    return Object.keys(out).length ? out : undefined;
};

const parsePickerEntriesByPreset = (raw: any, templates: LayoutTemplate[]): Record<CanvasAspectPreset, string[]> => {
    const fallback = (): Record<CanvasAspectPreset, string[]> => {
        const ids = templates.map((t) => String(t.id));
        return {
            horizontal: [...ids],
            vertical: [...ids],
            square: [...ids],
        };
    };

    if (!raw || typeof raw !== "object") return fallback();

    const out: Record<CanvasAspectPreset, string[]> = {
        horizontal: [],
        vertical: [],
        square: [],
    };
    for (const preset of PRESETS) {
        const list = (raw as any)[preset];
        if (!Array.isArray(list)) continue;
        out[preset] = list.map((x: any) => String(x ?? "").trim()).filter(Boolean);
    }
    if (!out.horizontal.length && !out.vertical.length && !out.square.length) return fallback();
    return out;
};

const parseDefaultMulticamVariantKeyByPreset = (raw: any): Partial<Record<CanvasAspectPreset, string>> | undefined => {
    if (!raw || typeof raw !== "object") return undefined;
    const out: Partial<Record<CanvasAspectPreset, string>> = {};
    for (const [k, v] of Object.entries(raw)) {
        const preset = asPreset(k);
        if (!preset) continue;
        const key = toStringOrNull(v);
        if (!key) continue;
        out[preset] = key;
    }
    return Object.keys(out).length ? out : undefined;
};

class LayoutPacksStoreImpl {
    packs: LayoutPack[] = [BUILTIN_LAYOUT_PACK];
    loading = false;
    error: string | null = null;
    hydrated = false;

    private unsubPacks: Unsubscribe | null = null;
    private unsubTemplatesByPackId: Map<string, Unsubscribe> = new Map();

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    teardown() {
        try {
            this.unsubPacks?.();
        } catch {}
        this.unsubPacks = null;

        for (const unsub of this.unsubTemplatesByPackId.values()) {
            try {
                unsub();
            } catch {}
        }
        this.unsubTemplatesByPackId.clear();
    }

    getPack(packId: string | null | undefined): LayoutPack | null {
        const id = String(packId ?? "").trim();
        if (!id) return null;
        return this.packs.find((p) => p.id === id) ?? null;
    }

    hasPack(packId: string | null | undefined): boolean {
        return Boolean(this.getPack(packId));
    }

    subscribe(opts: SubscribeOptions = {}) {
        this.teardown();
        this.loading = true;
        this.error = null;
        this.hydrated = false;

        const packsRef = collection(db, kLayoutPacksCollectionName);
        this.unsubPacks = onSnapshot(
            packsRef,
            (snap) => {
                const nextById = new Map<string, LayoutPack>();

                // Keep builtin pack unless Firestore provides a replacement for it.
                nextById.set(BUILTIN_LAYOUT_PACK.id, BUILTIN_LAYOUT_PACK);

                snap.forEach((d) => {
                    const packId = String(d.id ?? "").trim();
                    if (!packId) return;

                    const data: any = d.data() ?? {};

                    // Best-effort client-side filtering; security rules should be the main gate.
                    try {
                        const visibility = String(data.visibility ?? data.scope ?? "").trim().toLowerCase();
                        const workspaceId = String(data.workspaceId ?? "").trim();
                        const userId = String(data.userId ?? data.ownerUserId ?? "").trim();
                        if (visibility === "workspace" && opts.workspaceId && workspaceId && workspaceId !== opts.workspaceId) return;
                        if (visibility === "user" && opts.userId && userId && userId !== opts.userId) return;
                    } catch {}

                    const title = toStringOrNull(data.title ?? data.name) ?? (packId === DEFAULT_LAYOUT_PACK_ID ? "Default" : packId);

                    const templates: LayoutTemplate[] = [];
                    const rawTemplates = data.templates;
                    if (Array.isArray(rawTemplates)) {
                        for (const item of rawTemplates) {
                            if (!item || typeof item !== "object") continue;
                            const tid = toStringOrNull((item as any).id) ?? "";
                            const tmpl = parseTemplate(tid, item);
                            if (tmpl) templates.push(tmpl);
                        }
                    }

                    const pack: LayoutPack = {
                        id: packId,
                        title,
                        templates,
                        pickerEntriesByPreset: parsePickerEntriesByPreset(data.pickerEntriesByPreset, templates),
                        multicamVariantsByPreset: parseMulticamVariantsByPreset(data.multicamVariantsByPreset),
                        defaultMulticamVariantKeyByPreset: parseDefaultMulticamVariantKeyByPreset(
                            data.defaultMulticamVariantKeyByPreset
                        ),
                    };

                    nextById.set(packId, pack);
                });

                const nextPacks = Array.from(nextById.values()).sort((a, b) => {
                    if (a.id === DEFAULT_LAYOUT_PACK_ID) return -1;
                    if (b.id === DEFAULT_LAYOUT_PACK_ID) return 1;
                    return String(a.title ?? "").localeCompare(String(b.title ?? ""));
                });

                // Subscribe to templates subcollections for packs that don't embed templates.
                const packIds = new Set(nextPacks.map((p) => p.id));
                for (const existingId of Array.from(this.unsubTemplatesByPackId.keys())) {
                    if (packIds.has(existingId)) continue;
                    try {
                        this.unsubTemplatesByPackId.get(existingId)?.();
                    } catch {}
                    this.unsubTemplatesByPackId.delete(existingId);
                }

                for (const p of nextPacks) {
                    if (p.templates.length > 0) continue;
                    if (this.unsubTemplatesByPackId.has(p.id)) continue;
                    try {
                        const tmplRef = collection(db, kLayoutPacksCollectionName, p.id, "templates");
                        const unsub = onSnapshot(
                            tmplRef,
                            (tmplSnap) => {
                                const templates: LayoutTemplate[] = [];
                                tmplSnap.forEach((td) => {
                                    const tid = String(td.id ?? "").trim();
                                    if (!tid) return;
                                    const tmpl = parseTemplate(tid, td.data());
                                    if (tmpl) templates.push(tmpl);
                                });
                                templates.sort((a, b) => String(a.title ?? "").localeCompare(String(b.title ?? "")));

                                runInAction(() => {
                                    const idx = this.packs.findIndex((x) => x.id === p.id);
                                    if (idx < 0) return;
                                    const prev = this.packs[idx];
                                    const nextPack: LayoutPack = {
                                        ...prev,
                                        templates,
                                        pickerEntriesByPreset: prev.pickerEntriesByPreset ?? parsePickerEntriesByPreset({}, templates),
                                    };
                                    this.packs = [...this.packs.slice(0, idx), nextPack, ...this.packs.slice(idx + 1)];
                                });
                            },
                            () => {}
                        );
                        this.unsubTemplatesByPackId.set(p.id, unsub);
                    } catch {}
                }

                runInAction(() => {
                    this.packs = nextPacks;
                    this.loading = false;
                    this.hydrated = true;
                });
            },
            (error: any) => {
                console.error("[LayoutPacksStore] subscription error", error);
                if (error?.code === "permission-denied") opts.onAuthError?.();
                runInAction(() => {
                    this.loading = false;
                    this.error = String(error?.message ?? error ?? "Failed to load layout packs");
                    this.hydrated = true;
                });
            }
        );
    }
}

export const LayoutPacksStore = new LayoutPacksStoreImpl();
